import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useHeartbeat } from "@/hooks/useHeartbeat";
import { LayoutDashboard, Building2, ClipboardList, Users, Settings, LogOut, Leaf, FileText, FolderOpen, BookOpen, KeyRound, BadgeCheck, Menu, X, ShieldCheck } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ALL_ROLES, INTERNAL_ROLES, hasRole, userRoles, roleLabel } from "@/lib/roles";
import CoiPendingBanner from "@/components/CoiPendingBanner";
import IdleLogout from "@/components/IdleLogout";

const READERS = [...INTERNAL_ROLES, "partner"];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  useHeartbeat(!!user);

  // Close the mobile drawer whenever the user navigates to a new page.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const nav = [
    { to: "/app", label: "Assessment", icon: LayoutDashboard, roles: ALL_ROLES },
    { to: "/aziende", label: "Aziende", icon: Building2, roles: READERS },
    { to: "/assessment/nuovo", label: "Nuovo Assessment", icon: ClipboardList, roles: ["admin", "valuatore"] },
    { to: "/documenti", label: "Documenti", icon: FolderOpen, roles: ALL_ROLES },
    { to: "/registro", label: "Registro pubblico", icon: BadgeCheck, roles: READERS },
    { to: "/utenti", label: "Utenti", icon: Users, roles: ["admin"] },
    { to: "/impostazioni", label: "Impostazioni", icon: Settings, roles: ["admin", "resp_metodologia"] },
  ];

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-viva-50/60">
      {/* Mobile top bar — visible only below lg */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-slate-200 px-3 py-2 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2" data-testid="mobile-logo-home">
          <img src="/viva_logo.png" alt="VIVA ESG" className="w-8 h-8 object-contain" />
          <span className="font-heading font-bold text-viva-700 text-base">VIVA ESG</span>
        </a>
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Apri menu"
          data-testid="mobile-menu-open"
          className="p-2 rounded-lg text-slate-700 hover:bg-slate-100"
        >
          <Menu className="w-6 h-6" />
        </button>
      </header>

      {/* Backdrop for mobile drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-menu-backdrop"
          aria-hidden="true"
        />
      )}

      {/* Sidebar
          - lg+: static, 256px wide, always visible
          - <lg: slides in from the left as a drawer */}
      <aside
        className={[
          "fixed lg:static z-50 inset-y-0 left-0 w-64 shrink-0 bg-slate-50 border-r border-slate-200 flex flex-col",
          "transform transition-transform duration-200 ease-out",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          "lg:translate-x-0",
        ].join(" ")}
        data-testid="layout-sidebar"
      >
        {/* Drawer close button (mobile only) */}
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          aria-label="Chiudi menu"
          data-testid="mobile-menu-close"
          className="lg:hidden absolute top-2 right-2 p-2 rounded-lg text-slate-500 hover:bg-slate-100"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="px-4 py-5 border-b border-slate-100 bg-white">
          <a
            href="/"
            className="flex flex-col items-center text-center gap-2 group"
            data-testid="layout-logo-home-link"
            title="Vai alla home pubblica"
          >
            <img src="/viva_logo.png" alt="VIVA ESG" className="w-32 h-32 object-contain drop-shadow-sm group-hover:scale-105 transition-transform" />
            <div>
              <div className="font-heading font-bold text-viva-700 text-xl leading-tight">VIVA ESG</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold mt-0.5">Rating Platform</div>
            </div>
          </a>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto no-scrollbar">
          {nav
            .filter((n) => hasRole(user, ...n.roles))
            .map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/app"}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                    isActive
                      ? "bg-viva-50 text-viva-700"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                {label}
              </NavLink>
            ))}
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/manual/user.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-user-manual"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition text-slate-500 hover:bg-viva-50 hover:text-viva-700 mt-2 border-t border-slate-100 pt-3"
            title="Apri il Manuale Utente"
          >
            <BookOpen className="w-4 h-4" strokeWidth={1.75} />
            Manuale Utente
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/DT-RTG-01_Disciplinare_Tecnico_Rating_ESG_Rev00.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-disciplinare-tecnico"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition text-slate-500 hover:bg-viva-50 hover:text-viva-700"
            title="Apri il Disciplinare Tecnico Rating ESG"
          >
            <FileText className="w-4 h-4" strokeWidth={1.75} />
            Disciplinare Tecnico
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/manual/privacy.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-privacy-policy"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition text-slate-500 hover:bg-viva-50 hover:text-viva-700"
            title="Apri l'Informativa Privacy"
          >
            <ShieldCheck className="w-4 h-4" strokeWidth={1.75} />
            Informativa Privacy
          </a>
        </nav>
        {/* VIVA company footer */}
        <div className="px-4 py-3 border-t border-slate-100 bg-viva-50/60 text-[11px] leading-relaxed text-slate-600">
          <div className="font-heading font-bold text-viva-700 mb-1">VIVA ESG</div>
          <div className="text-[10px] text-slate-500 leading-snug">
            Via Eleonora Duse 5H<br />
            00197 Roma · P.IVA 17815411008<br />
            <a href="tel:+390683390629" className="hover:text-viva-700">Tel +39 06 83390629</a><br />
            <a href="tel:+393793021655" className="hover:text-viva-700">Mob +39 379 3021655</a>
          </div>
          <div className="mt-1.5 flex flex-col gap-0.5">
            <a href="https://www.vivaesg.it" target="_blank" rel="noopener noreferrer"
               className="text-viva-600 hover:text-viva-700 text-[10px] font-medium">
              www.vivaesg.it
            </a>
            <a href="mailto:tecnico@vivaesg.it" className="text-viva-600 hover:text-viva-700 text-[10px]">tecnico@vivaesg.it</a>
            <a href="mailto:amministrazione@vivaesg.it" className="text-viva-600 hover:text-viva-700 text-[10px]">amministrazione@vivaesg.it</a>
            <a href="mailto:commerciale@vivaesg.eu" className="text-viva-600 hover:text-viva-700 text-[10px]">commerciale@vivaesg.eu</a>
          </div>
        </div>
        <div className="px-3 py-3 border-t border-slate-100">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="w-9 h-9">
              <AvatarFallback className="bg-viva-500 text-white text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{userRoles(user).map(roleLabel).join(" · ")}</div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/cambio-password")}
            data-testid="change-password-button"
            className="w-full mt-2 justify-start text-slate-600 hover:text-slate-900"
          >
            <KeyRound className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Cambia password
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full justify-start text-slate-600 hover:text-slate-900"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.75} />
            Esci
          </Button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-y-auto pt-12 lg:pt-0">
        <CoiPendingBanner />
        <IdleLogout />
        <Outlet />
      </main>
    </div>
  );
}
